import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Theme-Modi:
 * - `'light'` / `'dark'`: User-Override, in localStorage persistiert.
 * - `'system'`: folgt der OS-Praeferenz via `prefers-color-scheme`.
 *
 * `resolvedTheme` ist der tatsaechlich angewendete Mode (`'light'` oder
 * `'dark'`), nachdem `'system'` ausgewertet wurde.
 */
export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'civiport-theme';

function readSavedTheme(): Theme {
  // Default = hell (sauberes Behoerden-Layout). Dark bleibt via Toggle waehlbar
  // und wird in localStorage persistiert.
  if (typeof window === 'undefined') return 'light';
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
  } catch {
    /* localStorage kann blockiert sein (Privacy-Mode) */
  }
  return 'light';
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyThemeClass(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps): JSX.Element {
  const [theme, setThemeState] = useState<Theme>(() => readSavedTheme());
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    readSavedTheme() === 'system' ? getSystemTheme() : (readSavedTheme() as ResolvedTheme),
  );

  // Wenn Theme oder System-Preference sich aendert, Class neu setzen.
  useEffect(() => {
    const next: ResolvedTheme = theme === 'system' ? getSystemTheme() : theme;
    setResolvedTheme(next);
    applyThemeClass(next);
  }, [theme]);

  // System-Preference-Listener (nur aktiv, wenn theme === 'system').
  useEffect(() => {
    if (theme !== 'system') return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent): void => {
      const next: ResolvedTheme = e.matches ? 'dark' : 'light';
      setResolvedTheme(next);
      applyThemeClass(next);
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    try {
      window.localStorage.setItem(STORAGE_KEY, t);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Festes Theme ohne Speichern und ohne Klasse am Dokument, z. B. fuer Kacheln im hellen oeffentlichen Portal. */
export function FixedThemeProvider({ theme, children }: { theme: ResolvedTheme; children: ReactNode }): JSX.Element {
  const value = useMemo<ThemeContextValue>(() => ({ theme, resolvedTheme: theme, setTheme: () => undefined }), [theme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme muss innerhalb von <ThemeProvider> verwendet werden.');
  }
  return ctx;
}
